/** @format */

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  Grid,
  Modal,
  Button,
  Skeleton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LockIcon from "@mui/icons-material/Lock";
import AnimationWithTitle from "./AnimationWithTitle";
import DataNotFound from "../animations/not-found.json";
import { useSelector } from "react-redux";
import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";
import usePaymentModule from "./PaymentModule";
import apis from "../../utils/apis";

const TopSkillsInterviewQuestions = ({
  topSkillsQuestions,
  loading,
  error,
  topSkills = [],
  userName = "",
  userEmail = "",
  onFetchQuestions,
  isSubscribed,
  onSubscriptionActivated,
  onInitiateSubscription,
}) => {
  const [searchText, setSearchText] = useState("");
  const [isPaid, setIsPaid] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const Razor_Pay_key = useFeatureFlag("Razor_Pay_key");
  const New_Theme = useFeatureFlag("New_Theme");
  const amount = useFeatureFlag(
    "Techrelevance_TopSkill_Interview_Question_Price"
  );
  const resultId = useSelector(
    (state) => state?.ResultReducer?.resultId || null
  );

  // Check if content is free (amount <= 0)
  const isFree = !amount || amount <= 0;

  const handleSearch = (event) => {
    setSearchText(event.target.value.toLowerCase());
  };

  const handleModalOpen = () => {
    setPaymentLoading(true);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setPaymentLoading(false);
    setModalOpen(false);
    // Explicitly restore body overflow when modal closes
    setTimeout(() => {
      document.body.style.overflow = "";
      document.body.style.height = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";
    }, 100);
  };

  // Initialize payment module
  const { initiatePayment } = usePaymentModule({
    amount,
    userName,
    userEmail,
    razorpayKey: Razor_Pay_key,
    themeColor: New_Theme ? "#21175E" : "#6652E4",
    resultId,
    onSuccess: async () => {
      // console.log(
      //   "Subscription payment saved successfully, verifying subscription..."
      // );
      // Note: Modal is already shown by PaymentModule during save-subscription-payment

      // Unlock access immediately after successful payment
      setIsPaid(true);
      setPaymentLoading(false);
      setModalOpen(false);

      // Fetch questions after successful subscription
      if (typeof onFetchQuestions === "function") {
        try {
          await onFetchQuestions(topSkills);
        } catch (fetchError) {
          console.error(
            "Failed to fetch top skills interview questions post-payment:",
            fetchError
          );
        }
      }

      if (typeof onSubscriptionActivated === "function") {
        onSubscriptionActivated();
      }
    },
    onFailure: (error) => {
      console.error("Payment or subscription save failed:", error);
      setPaymentLoading(false);
      setModalOpen(false);
    },
    onModalOpen: handleModalOpen,
    onModalClose: handleModalClose,
  });

  const handlePayment = () => {
    if (typeof onInitiateSubscription === "function") {
      onInitiateSubscription();
    } else {
      initiatePayment();
    }
  };

  // Check user subscription on component mount
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        setSubscriptionLoading(true);
        const response = await apis.GET({
          endpoint: "/check-user-subscription",
          options: { type: "json" },
        });

        if (response?.data?.hasActiveSubscription) {
          // console.log("Active subscription found:", response.data);
          setIsPaid(true);

          // Fetch questions if user has active subscription
          if (onFetchQuestions && topSkills.length > 0) {
            onFetchQuestions(topSkills);
          }
        } else {
          // console.log("No active subscription found");
          setIsPaid(false);
        }
      } catch (error) {
        console.error("Error checking subscription:", error);
        setIsPaid(false);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    if (typeof isSubscribed === "boolean") {
      setIsPaid(isSubscribed);
      if (isSubscribed) {
        setSubscriptionLoading(false);
        setPaymentLoading(false);
        setModalOpen(false);
      }
      if (isSubscribed || isFree) {
        return;
      }
    }

    // Only check subscription if content is not free
    if (!isFree) {
      checkSubscription();
    } else {
      // If content is free, no need to check subscription
      setSubscriptionLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFree, isSubscribed]);

  // Auto-unlock if free (questions already fetched in TechRelevance)
  useEffect(() => {
    if (isFree && !isPaid) {
      // console.log("Content is free, auto-unlocking...");
      setIsPaid(true);
    }
  }, [isFree, isPaid]);

  // Restore body overflow when modal closes
  useEffect(() => {
    if (!modalOpen) {
      // Use setTimeout to ensure Material-UI has finished its cleanup
      const timer = setTimeout(() => {
        document.body.style.overflow = "";
        document.body.style.height = "";
        document.body.style.paddingRight = "";
        // Also check documentElement
        document.documentElement.style.overflow = "";
        document.documentElement.style.height = "";
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [modalOpen]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Restore body scroll when component unmounts
      document.body.style.overflow = "";
      document.body.style.height = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";
    };
  }, []);

  // Function to highlight technical terms in answers using dynamic top skills
  const highlightTechnicalTerms = (text) => {
    if (!text) return "";
    if (!topSkills || topSkills.length === 0) return text;

    // Extract skill names from topSkills array
    const skillNames = topSkills
      .map((skill) => skill?.name)
      .filter((name) => name && name.trim() !== "");

    if (skillNames.length === 0) return text;

    // Escape special regex characters in skill names
    const escapeRegex = (string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    };

    // Helper function to check if a position is already inside a highlight span
    const isInsideHighlight = (text, index) => {
      // Check backwards for opening span tag
      const beforeText = text.substring(Math.max(0, index - 500), index);
      const lastOpenSpan = beforeText.lastIndexOf("<span");
      const lastCloseSpan = beforeText.lastIndexOf("</span>");
      return lastOpenSpan > lastCloseSpan;
    };

    // Helper function to check if two ranges overlap
    const rangesOverlap = (start1, end1, start2, end2) => {
      return start1 < end2 && start2 < end1;
    };

    // Collect ALL matches from ALL skills first (before any modifications)
    const allMatches = [];

    for (const skillName of skillNames) {
      const trimmedSkillName = skillName.trim();
      if (!trimmedSkillName) continue;

      const escapedSkillName = escapeRegex(trimmedSkillName);
      
      // For multi-word skills (containing spaces), don't use word boundaries
      // For single-word skills, use word boundaries to avoid partial matches
      const hasSpaces = trimmedSkillName.includes(" ");
      const pattern = hasSpaces
        ? new RegExp(escapedSkillName, "gi")
        : new RegExp(`\\b${escapedSkillName}\\b`, "gi");

      let match;
      // Reset regex lastIndex to ensure we search from the beginning
      pattern.lastIndex = 0;
      while ((match = pattern.exec(text)) !== null) {
        const matchIndex = match.index;
        const matchLength = match[0].length;
        const matchText = match[0];

        // Skip if this match is already inside a highlight span
        if (!isInsideHighlight(text, matchIndex)) {
          allMatches.push({
            index: matchIndex,
            endIndex: matchIndex + matchLength,
            length: matchLength,
            text: matchText,
            skillName: trimmedSkillName,
          });
        }
      }
    }

    // Remove overlapping matches - if two matches overlap, keep the longer one
    // Sort by length descending first, then by index ascending
    allMatches.sort((a, b) => {
      if (b.length !== a.length) return b.length - a.length;
      return a.index - b.index;
    });

    const filteredMatches = [];
    for (const match of allMatches) {
      let hasOverlap = false;
      for (const existingMatch of filteredMatches) {
        if (
          rangesOverlap(
            match.index,
            match.endIndex,
            existingMatch.index,
            existingMatch.endIndex
          )
        ) {
          hasOverlap = true;
          break;
        }
      }
      if (!hasOverlap) {
        filteredMatches.push(match);
      }
    }

    // Sort filtered matches by index in descending order (process from end to start)
    // This prevents index shifting when we insert HTML
    filteredMatches.sort((a, b) => b.index - a.index);

    // Now process all matches in reverse order (from end to start)
    let highlightedText = text;
    for (const match of filteredMatches) {
      const { index, length, text: matchText } = match;
      
      // Double-check we're not inside a highlight (in case of nested processing)
      if (!isInsideHighlight(highlightedText, index)) {
        const before = highlightedText.substring(0, index);
        const after = highlightedText.substring(index + length);
        const highlighted = `<span style="background-color: rgba(102,82,228,0.1); padding: 2px 6px; border-radius: 4px; color: #6652E4; font-weight: 600;">${matchText}</span>`;
        highlightedText = before + highlighted + after;
      }
    }

    return highlightedText;
  };

  // Extract interviewQuestions array from the API response
  // API response format: { interviewQuestions: [...] }
  const questions =
    topSkillsQuestions?.interviewQuestions ||
    (Array.isArray(topSkillsQuestions) ? topSkillsQuestions : []);

  const filteredQuestions = questions.filter(
    (item) =>
      item.question?.toLowerCase().includes(searchText) ||
      item.answer?.toLowerCase().includes(searchText)
  );

  // Removed this useEffect as it causes infinite loop
  // Questions should be fetched by parent component (TechRelevance)

  // Show loading while checking subscription or fetching questions
  if (subscriptionLoading || (loading && isPaid)) {
    return (
      <Box sx={{ width: "100%" }}>
        {/* Search Bar Skeleton */}
        <Skeleton
          variant="rounded"
          height={56}
          sx={{ mb: { xs: 2, sm: 2.5, md: 3 }, borderRadius: "12px" }}
        />

        {/* Question Cards Skeleton */}
        <Grid
          container
          spacing={{ xs: 2, sm: 2.5, md: 3 }}
          sx={{ width: "100%" }}
        >
          {[1, 2, 3, 4].map((item) => (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={6}
              key={item}
              sx={{ display: "flex", height: "100%" }}
            >
              <Box
                sx={{
                  width: "100%",
                  backgroundColor: "#fff",
                  borderRadius: { xs: "16px", sm: "20px", md: "24px" },
                  border: "1px solid rgba(102,82,228,0.15)",
                  p: { xs: 2, sm: 2.5, md: 3 },
                }}
              >
                {/* Question Number and Title Skeleton */}
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <Skeleton
                    variant="circular"
                    width={44}
                    height={44}
                    sx={{ flexShrink: 0 }}
                  />
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={30}
                    sx={{ mt: 1 }}
                  />
                </Box>

                {/* Answer Skeleton */}
                <Skeleton variant="text" width="100%" height={20} />
                <Skeleton variant="text" width="95%" height={20} />
                <Skeleton variant="text" width="90%" height={20} />
                <Skeleton variant="text" width="85%" height={20} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Show error only if paid and there's an error or no questions
  if (isPaid && (error || !topSkillsQuestions || questions.length === 0)) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "300px",
          p: 3,
        }}
      >
        <AnimationWithTitle
          animationData={DataNotFound}
          title="No questions available"
          subtitle="Unable to fetch interview questions at this time."
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      {/* Payment Loading Modal */}
      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          // Explicitly restore body overflow when modal closes
          setTimeout(() => {
            document.body.style.overflow = "";
            document.body.style.height = "";
            document.body.style.paddingRight = "";
            document.documentElement.style.overflow = "";
            document.documentElement.style.height = "";
          }, 100);
        }}
        aria-labelledby="payment-loading-modal"
        aria-describedby="payment-processing"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
          position="fixed"
        >
          <CircularProgress sx={{ color: "#fff" }} />
        </Box>
      </Modal>

      {/* Payment Overlay - Show when not paid and not free */}
      {!isPaid && !isFree && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(8px)",
            borderRadius: "24px",
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              p: { xs: 3, sm: 4, md: 5 },
              maxWidth: "500px",
              maxHeight: "700px",
            }}
          >
            <Box
              sx={{
                width: { xs: "80px", sm: "100px" },
                height: { xs: "80px", sm: "100px" },
                borderRadius: "50%",
                backgroundColor: "rgba(102,82,228,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
                mb: 3,
              }}
            >
              <LockIcon
                sx={{
                  fontSize: { xs: "40px", sm: "50px" },
                  color: "#6652E4",
                }}
              />
            </Box>

            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: { xs: "20px", sm: "24px", md: "28px" },
                fontWeight: 700,
                color: "#262655",
                mb: 2,
              }}
            >
              Unlock Interview Questions
            </Typography>

            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: { xs: "14px", sm: "15px", md: "16px" },
                fontWeight: 400,
                color: "#666",
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              Get monthly access to comprehensive interview questions for your
              top skills. Prepare better and ace your next interview!
            </Typography>

            <Button
              variant="contained"
              onClick={handlePayment}
              disabled={paymentLoading}
              startIcon={
                paymentLoading ? <CircularProgress size={20} /> : <LockIcon />
              }
              sx={{
                backgroundColor: "#6652E4",
                color: "#fff",
                fontFamily: "Poppins, sans-serif",
                fontSize: { xs: "14px", sm: "16px" },
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "12px",
                px: { xs: 3, sm: 4 },
                py: { xs: 1.5, sm: 2 },
                boxShadow: "0px 4px 12px rgba(102,82,228,0.3)",
                "&:hover": {
                  backgroundColor: "#5542d4",
                  boxShadow: "0px 6px 16px rgba(102,82,228,0.4)",
                },
                "&:disabled": {
                  backgroundColor: "#ccc",
                },
              }}
            >
              {paymentLoading ? "Processing..." : `Subscribe ₹${amount}/month`}
            </Button>
          </Box>
        </Box>
      )}
      {(isPaid || isFree) && (
        <Box
          sx={{
            filter: !isPaid && !isFree ? "blur(6px)" : "none",
            pointerEvents: !isPaid && !isFree ? "none" : "auto",
            userSelect: !isPaid && !isFree ? "none" : "auto",
          }}
        >
          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Search questions or answers..."
            value={searchText}
            onChange={handleSearch}
            sx={{
              mb: { xs: 2, sm: 2.5, md: 3 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#fff",
                fontSize: { xs: "14px", sm: "15px", md: "16px" },
                "& fieldset": {
                  borderColor: "rgba(102,82,228,0.25)",
                  borderWidth: "1px",
                },
                "&:hover fieldset": {
                  borderColor: "#6652E4",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#6652E4",
                  borderWidth: "2px",
                },
              },
              "& .MuiInputBase-input": {
                color: "#020D17",
                py: { xs: 1.5, sm: 1.75, md: 2 },
                "&::placeholder": {
                  color: "#888",
                  opacity: 1,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: "#6652E4",
                      fontSize: { xs: "20px", sm: "22px", md: "24px" },
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />

          {/* Questions Cards */}
          {filteredQuestions.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "200px",
                p: { xs: 2, sm: 3, md: 4 },
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: { xs: "14px", sm: "15px", md: "16px" },
                  color: "#888",
                  textAlign: "center",
                }}
              >
                No questions found matching your search.
              </Typography>
            </Box>
          ) : (
            <Grid
              container
              spacing={{ xs: 2, sm: 2.5, md: 3 }}
              sx={{ width: "100%" }}
            >
              {filteredQuestions.map((item, index) => {
                const itemId = item._id || item.index || index;
                const questionNumber = item.index || index + 1;
                const highlightedAnswer = highlightTechnicalTerms(item.answer);

                return (
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={6}
                    key={itemId}
                    sx={{
                      display: "flex",
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        backgroundColor: "#fff",
                        borderRadius: { xs: "16px", sm: "20px", md: "24px" },
                        border: "1px solid rgba(102,82,228,0.15)",
                        boxShadow: "0px 2px 8px rgba(102,82,228,0.08)",
                        p: { xs: 2, sm: 2.5, md: 3 },
                        display: "flex",
                        flexDirection: "column",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0px 4px 16px rgba(102,82,228,0.15)",
                          borderColor: "rgba(102,82,228,0.3)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      {/* Question Section */}
                      <Box
                        sx={{
                          mb: { xs: 2, sm: 2.5, md: 3 },
                          pb: { xs: 2, sm: 2.5, md: 3 },
                          borderBottom: "2px solid rgba(102,82,228,0.1)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: { xs: 1.5, sm: 2 },
                            mb: { xs: 1.5, sm: 2 },
                          }}
                        >
                          <Box
                            sx={{
                              minWidth: { xs: "36px", sm: "40px", md: "44px" },
                              height: { xs: "36px", sm: "40px", md: "44px" },
                              borderRadius: "50%",
                              backgroundColor: "rgba(102,82,228,0.1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Poppins, sans-serif",
                                fontSize: {
                                  xs: "16px",
                                  sm: "18px",
                                  md: "20px",
                                },
                                fontWeight: 700,
                                color: "#6652E4",
                              }}
                            >
                              {questionNumber}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: {
                                xs: "15px",
                                sm: "16px",
                                md: "18px",
                                lg: "19px",
                              },
                              fontWeight: 600,
                              color: "#262655",
                              lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
                              flex: 1,
                            }}
                          >
                            {item.question}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Answer Section */}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          component="div"
                          sx={{
                            fontFamily: "Poppins, sans-serif",
                            fontSize: {
                              xs: "14px",
                              sm: "15px",
                              md: "16px",
                              lg: "17px",
                            },
                            fontWeight: 400,
                            color: "#232323",
                            lineHeight: { xs: 1.6, sm: 1.7, md: 1.8 },
                            "& span": {
                              display: "inline",
                              margin: "0 1px",
                            },
                          }}
                          dangerouslySetInnerHTML={{
                            __html: highlightedAnswer,
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TopSkillsInterviewQuestions;
